// central.eu — console view. Quote composer as a declarative dees-element:
// state-driven bill of materials + demand-adjusted quote, styles in TypeScript.

import {
  DeesElement,
  customElement,
  html,
  css,
  state,
  cssManager,
  type TemplateResult,
} from '@design.estate/dees-element';
import type * as interfaces from '../../ts_interfaces/index.js';
import * as plugins from '../plugins.js';
import { tokenHostVars, baseStyles } from '../ceu-theme.js';
import { icon } from '../ceu-icons.js';
import { CATALOG, DEMAND, REGIONS } from '../ceu-data.js';
import { calculateQuote, calculateQuoteLine } from '../../ts/quote-catalog.js';

type TItemType = interfaces.TQuoteItemType;
interface IItem extends interfaces.IQuoteStackSelection {
  id: number;
  type: TItemType;
  region: string;
  coreType?: 'shared' | 'dedicated';
  cores?: number;
  mem?: number;
  replicas?: number;
  count?: number;
  gib?: number;
}

const TYPES: Record<TItemType, { sym: string; nm: string; pr: string }> = {
  container: { sym: 'Box', nm: 'Container workload', pr: `from €${CATALOG.shared.toFixed(2)}/core` },
  node: { sym: 'Server', nm: 'Dedicated node', pr: `€${CATALOG.node}/node` },
  block: { sym: 'HardDrive', nm: 'Block storage', pr: `€${CATALOG.blk}/GiB` },
  object: { sym: 'Database', nm: 'Object storage', pr: `€${CATALOG.obj}/GiB` },
  ip: { sym: 'Globe', nm: 'Public IPv4', pr: `€${CATALOG.ip.toFixed(2)}/addr` },
};

const DEFAULTS: Record<TItemType, () => Partial<IItem>> = {
  container: () => ({ coreType: 'shared', cores: 4, mem: 8, replicas: 2, region: 'bre1' }),
  node: () => ({ count: 1, region: 'bre1' }),
  block: () => ({ gib: 100, region: 'bre1' }),
  object: () => ({ gib: 250, region: 'bre1' }),
  ip: () => ({ count: 1, region: 'bre1' }),
};

const regionById = (id: string) => REGIONS.find((r) => r.id === id)!;
const isLive = (id: string) => regionById(id).state === 'live';
const eur = (n: number) => '€' + n.toLocaleString('en-IE', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

@customElement('ceu-console')
export class CeuConsole extends DeesElement {
  @state() accessor stack: IItem[] = [];
  @state() accessor term = 0;
  @state() accessor modalOpen = false;
  @state() accessor modalStage: 'form' | 'confirm' = 'form';
  @state() accessor modalBusy = false;
  @state() accessor modalError = '';

  private uid = 1;
  private demandKey = 'high';
  private modalRef = '';
  private modalIndicativeUntil: Date | null = null;

  public static styles = [
    cssManager.defaultStyles,
    tokenHostVars,
    baseStyles,
    css`
      :host {
        display: block;
        min-height: 100vh;
        font-family: var(--ff-text);
        letter-spacing: -0.01em;
        background: var(--canvas);
        color: var(--ink);
      }
      .label {
        font-family: var(--ff-mono);
        font-size: 11px;
        letter-spacing: 0.04em;
        text-transform: uppercase;
        color: var(--muted);
      }
      /* Top bar */
      .c-top {
        position: sticky;
        top: 0;
        z-index: 60;
        background: color-mix(in oklab, var(--canvas) 72%, transparent);
        backdrop-filter: saturate(1.8) blur(20px);
        -webkit-backdrop-filter: saturate(1.8) blur(20px);
        border-bottom: 1px solid var(--hairline-2);
      }
      .c-top-inner {
        max-width: var(--maxw);
        margin: 0 auto;
        display: flex;
        align-items: center;
        gap: 18px;
        padding: 13px var(--pad-x);
      }
      .c-brand {
        display: flex;
        align-items: baseline;
        gap: 9px;
        font-family: var(--ff-display);
        font-weight: 600;
        font-size: 17px;
        letter-spacing: -0.02em;
      }
      .c-brand .dot {
        width: 7px;
        height: 7px;
        background: var(--eu);
        border-radius: 50%;
        transform: translateY(-2px);
      }
      .c-brand .tld {
        color: var(--muted);
        font-weight: 400;
      }
      .c-brand .seg-label {
        font-family: var(--ff-mono);
        font-size: 11px;
        letter-spacing: 0.04em;
        text-transform: uppercase;
        color: var(--muted);
        border-left: 1px solid var(--hairline);
        padding-left: 11px;
        margin-left: 3px;
        align-self: center;
      }
      .c-env {
        display: inline-flex;
        align-items: center;
        gap: 8px;
        font-family: var(--ff-mono);
        font-size: 11px;
        background: var(--panel-2);
        border: 1px solid var(--hairline-2);
        padding: 6px 12px;
        border-radius: var(--pill);
        color: var(--ink-2);
      }
      .c-env .pip {
        width: 7px;
        height: 7px;
        border-radius: 50%;
        background: var(--green);
      }
      .c-env b {
        color: var(--ink);
        font-weight: 600;
      }
      .c-top-right {
        margin-left: auto;
        display: flex;
        align-items: center;
        gap: 10px;
      }
      .c-ea {
        font-size: 12px;
        font-weight: 500;
        color: var(--amber);
        background: color-mix(in oklab, var(--amber) 12%, transparent);
        padding: 5px 12px;
        border-radius: var(--pill);
      }
      .c-iconbtn {
        width: 34px;
        height: 34px;
        border: 1px solid var(--hairline-2);
        border-radius: 50%;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        color: var(--ink-2);
        background: var(--card);
      }
      .c-iconbtn:hover {
        background: var(--panel-2);
        color: var(--ink);
      }
      .c-iconbtn .lucide {
        width: 16px;
        height: 16px;
      }
      .c-acct {
        display: inline-flex;
        align-items: center;
        gap: 9px;
        font-size: 13px;
        font-weight: 500;
        color: var(--ink-2);
        background: var(--card);
        border: 1px solid var(--hairline-2);
        border-radius: var(--pill);
        padding: 4px 13px 4px 4px;
      }
      .c-acct .ava {
        width: 26px;
        height: 26px;
        background: var(--eu);
        color: #fff;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        font-weight: 600;
        font-size: 11px;
        border-radius: 50%;
        font-family: var(--ff-mono);
      }
      @media (max-width: 760px) {
        .c-acct,
        .c-env {
          display: none;
        }
      }

      /* Advisory */
      .c-advisory-inner {
        max-width: var(--maxw);
        margin: 18px auto 0;
        padding: 0 var(--pad-x);
      }
      .c-advisory-card {
        display: grid;
        grid-template-columns: 1fr auto;
        gap: 18px 28px;
        align-items: center;
        background: color-mix(in oklab, var(--amber) 9%, var(--card));
        border: 1px solid color-mix(in oklab, var(--amber) 28%, transparent);
        border-radius: var(--radius);
        padding: 18px 24px;
        box-shadow: var(--shadow-sm);
      }
      @media (max-width: 900px) {
        .c-advisory-card {
          grid-template-columns: 1fr;
        }
      }
      .c-adv-tag {
        font-size: 13px;
        font-weight: 600;
        color: var(--amber);
        display: flex;
        align-items: center;
        gap: 9px;
        margin-bottom: 7px;
      }
      .c-adv-tag .pulse-dot {
        width: 9px;
        height: 9px;
        border-radius: 50%;
        background: var(--amber);
      }
      .c-adv-text {
        font-size: 14px;
        color: var(--ink-2);
        line-height: 1.5;
        max-width: 86ch;
      }
      .c-adv-text b {
        color: var(--ink);
        font-weight: 600;
      }
      .c-adv-idx {
        font-family: var(--ff-mono);
        text-align: right;
        white-space: nowrap;
      }
      .c-adv-idx .big {
        font-size: 32px;
        font-weight: 600;
        color: var(--amber);
        letter-spacing: -0.02em;
        display: block;
      }
      .c-adv-idx .lbl {
        font-size: 10px;
        letter-spacing: 0.06em;
        text-transform: uppercase;
        color: var(--muted);
      }

      /* Frame */
      .c-main {
        max-width: var(--maxw);
        margin: 0 auto;
        width: 100%;
        padding: 40px var(--pad-x) 96px;
        display: grid;
        grid-template-columns: 1fr 400px;
        gap: 28px;
        align-items: start;
      }
      @media (max-width: 1080px) {
        .c-main {
          grid-template-columns: 1fr;
        }
      }
      .c-composer {
        display: flex;
        flex-direction: column;
        gap: 20px;
      }
      .c-head .idx {
        font-family: var(--ff-mono);
        font-size: 11px;
        letter-spacing: 0.04em;
        text-transform: uppercase;
        color: var(--muted);
      }
      .c-head h1 {
        margin: 12px 0 0;
        font-family: var(--ff-display);
        font-weight: 600;
        font-size: clamp(30px, 3.6vw, 44px);
        line-height: 1.04;
        letter-spacing: -0.03em;
      }
      .c-head p {
        margin: 13px 0 0;
        color: var(--muted);
        max-width: 62ch;
        font-size: 15px;
        line-height: 1.55;
      }
      .c-capboard,
      .c-palette,
      .c-item,
      .c-quote {
        background: var(--card);
        border: 1px solid var(--hairline-2);
        border-radius: var(--radius);
        box-shadow: var(--shadow-sm);
        overflow: hidden;
      }
      .c-capboard-head {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 16px 22px 12px;
      }
      .c-capboard-head .t {
        font-family: var(--ff-display);
        font-size: 16px;
        font-weight: 600;
        letter-spacing: -0.02em;
      }
      .c-capboard-head .mut {
        font-family: var(--ff-mono);
        font-size: 11px;
        color: var(--muted);
      }
      .c-caprow {
        display: grid;
        grid-template-columns: 110px 1fr 140px;
        gap: 16px;
        align-items: center;
        padding: 12px 22px;
        border-top: 1px solid var(--hairline-2);
        font-family: var(--ff-mono);
        font-size: 12px;
      }
      .c-caprow .rc {
        display: flex;
        flex-direction: column;
        gap: 1px;
      }
      .c-caprow .rc .code {
        color: var(--ink);
        font-weight: 600;
      }
      .c-caprow .rc .city {
        color: var(--muted);
        font-size: 10px;
      }
      .c-captrack {
        height: 8px;
        background: var(--panel-2);
        border-radius: var(--pill);
        overflow: hidden;
      }
      .c-capfill {
        height: 100%;
        background: var(--eu);
        border-radius: var(--pill);
      }
      .c-capfill.hot {
        background: var(--amber);
      }
      .c-capfill.crit {
        background: #ff453a;
      }
      .c-caprow .rstate {
        text-align: right;
        font-size: 10px;
      }
      .c-caprow .rstate .pct {
        color: var(--ink);
        font-weight: 600;
        font-size: 12px;
      }
      .c-caprow .rstate .tag {
        color: var(--muted);
      }
      .c-caprow .rstate .tag.live {
        color: var(--green);
      }
      .c-caprow .rstate .tag.build {
        color: var(--amber);
      }
      .c-palette {
        padding: 16px;
        display: grid;
        grid-template-columns: repeat(5, 1fr);
        gap: 10px;
      }
      @media (max-width: 720px) {
        .c-palette {
          grid-template-columns: repeat(2, 1fr);
        }
      }
      .c-palette-label {
        grid-column: 1 / -1;
        padding: 2px 6px 6px;
        font-family: var(--ff-display);
        font-size: 16px;
        font-weight: 600;
        letter-spacing: -0.02em;
      }
      .c-add {
        display: flex;
        flex-direction: column;
        gap: 9px;
        align-items: flex-start;
        padding: 15px 14px;
        border: 1px solid var(--hairline-2);
        border-radius: var(--radius-sm);
        background: var(--card-2);
        text-align: left;
        transition: transform 0.14s ease, box-shadow 0.14s ease, background 0.14s ease;
      }
      .c-add:hover {
        transform: translateY(-2px);
        box-shadow: var(--shadow-sm);
        background: var(--card);
      }
      .c-add .sym {
        color: var(--eu);
        background: color-mix(in oklab, var(--eu) 12%, transparent);
        width: 32px;
        height: 32px;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        border-radius: var(--radius-xs);
      }
      .c-add .sym .lucide {
        width: 18px;
        height: 18px;
      }
      .c-add .nm {
        font-family: var(--ff-display);
        font-weight: 600;
        font-size: 13.5px;
        line-height: 1.2;
        letter-spacing: -0.01em;
      }
      .c-add .pr {
        font-family: var(--ff-mono);
        font-size: 10px;
        color: var(--muted);
      }
      .c-bom {
        display: flex;
        flex-direction: column;
        gap: 14px;
      }
      .c-bom-empty {
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 10px;
        padding: 44px 26px;
        text-align: center;
        border: 1px dashed var(--hairline);
        border-radius: var(--radius);
        background: var(--card-2);
      }
      .c-bom-empty .sym {
        color: var(--eu);
        background: color-mix(in oklab, var(--eu) 12%, transparent);
        width: 40px;
        height: 40px;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        border-radius: var(--radius-xs);
      }
      .c-bom-empty .sym .lucide {
        width: 20px;
        height: 20px;
      }
      .c-bom-empty .t {
        font-family: var(--ff-display);
        font-size: 17px;
        font-weight: 600;
        letter-spacing: -0.02em;
        color: var(--ink);
      }
      .c-bom-empty p {
        margin: 0;
        max-width: 46ch;
        font-size: 13.5px;
        line-height: 1.6;
        color: var(--ink-2);
      }
      .c-bom-empty .hint {
        display: inline-flex;
        align-items: center;
        gap: 7px;
        margin-top: 4px;
        font-family: var(--ff-mono);
        font-size: 11px;
        letter-spacing: 0.04em;
        text-transform: uppercase;
        color: var(--muted);
      }
      .c-bom-empty .hint .lucide {
        width: 13px;
        height: 13px;
      }
      .c-quote-empty {
        padding: 22px;
        border-top: 1px solid var(--hairline-2);
      }
      .c-quote-empty .t {
        font-size: 13px;
        font-weight: 600;
        color: var(--ink);
        margin-bottom: 6px;
      }
      .c-quote-empty p {
        margin: 0;
        font-size: 12.5px;
        line-height: 1.6;
        color: var(--muted);
      }
      .c-quote-head .tick.idle {
        background: var(--hairline);
      }
      .c-total .grand.idle .amt {
        color: var(--muted);
      }
      .c-form-error {
        grid-column: 1 / -1;
        display: flex;
        align-items: flex-start;
        gap: 9px;
        padding: 11px 13px;
        border: 1px solid color-mix(in oklab, var(--amber) 45%, transparent);
        background: color-mix(in oklab, var(--amber) 12%, transparent);
        border-radius: var(--radius-xs);
        font-size: 12.5px;
        line-height: 1.5;
        color: var(--ink);
      }
      .c-form-error .lucide {
        width: 15px;
        height: 15px;
        flex: none;
        color: var(--amber);
        margin-top: 1px;
      }
      .c-item-head {
        display: grid;
        grid-template-columns: 38px 1fr auto auto;
        gap: 14px;
        align-items: center;
        padding: 16px 18px;
      }
      .c-item-sym {
        color: var(--eu);
        background: color-mix(in oklab, var(--eu) 12%, transparent);
        width: 36px;
        height: 36px;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        border-radius: var(--radius-xs);
      }
      .c-item-sym .lucide {
        width: 19px;
        height: 19px;
      }
      .c-item-title {
        display: flex;
        flex-direction: column;
        gap: 2px;
        min-width: 0;
      }
      .c-item-title .nm {
        font-family: var(--ff-display);
        font-weight: 600;
        font-size: 16px;
        letter-spacing: -0.02em;
      }
      .c-item-title .meta {
        font-family: var(--ff-mono);
        font-size: 11px;
        color: var(--muted);
      }
      .c-item-price {
        font-family: var(--ff-mono);
        text-align: right;
        display: flex;
        flex-direction: column;
        gap: 2px;
      }
      .c-item-price .now {
        font-size: 15px;
        font-weight: 600;
        color: var(--ink);
      }
      .c-item-price .base {
        font-size: 10px;
        color: var(--muted);
      }
      .c-item-rm {
        color: var(--muted);
        width: 30px;
        height: 30px;
        border-radius: 50%;
        display: inline-flex;
        align-items: center;
        justify-content: center;
      }
      .c-item-rm:hover {
        color: var(--ink);
        background: var(--panel-2);
      }
      .c-item-rm .lucide {
        width: 16px;
        height: 16px;
      }
      .c-item-body {
        display: grid;
        grid-template-columns: repeat(12, 1fr);
        gap: 18px 22px;
        padding: 6px 18px 20px;
        border-top: 1px solid var(--hairline-2);
      }
      .c-ctl {
        display: flex;
        flex-direction: column;
        gap: 9px;
      }
      .c-ctl.span3 {
        grid-column: span 3;
      }
      .c-ctl.span6 {
        grid-column: span 6;
      }
      @media (max-width: 720px) {
        .c-ctl.span3,
        .c-ctl.span6 {
          grid-column: span 6;
        }
      }
      .c-ctl-key {
        display: flex;
        justify-content: space-between;
        align-items: baseline;
        font-size: 12px;
        font-weight: 500;
        color: var(--muted);
      }
      .c-ctl-key .v {
        color: var(--ink);
        font-family: var(--ff-mono);
        font-size: 12px;
      }
      input[type='range'] {
        width: 100%;
        margin: 0;
        height: 22px;
        accent-color: var(--eu);
        -webkit-appearance: none;
        appearance: none;
        background: transparent;
      }
      input[type='range']::-webkit-slider-runnable-track {
        height: 4px;
        border-radius: var(--pill);
        background: var(--panel-2);
      }
      input[type='range']::-webkit-slider-thumb {
        -webkit-appearance: none;
        appearance: none;
        width: 18px;
        height: 18px;
        margin-top: -7px;
        border-radius: 50%;
        background: #fff;
        box-shadow: 0 1px 4px rgba(0, 0, 0, 0.25);
        cursor: pointer;
      }
      .c-stepper {
        display: inline-flex;
        align-items: center;
        background: var(--panel-2);
        border-radius: var(--radius-xs);
        padding: 3px;
        align-self: flex-start;
        gap: 2px;
      }
      .c-stepper button {
        width: 32px;
        height: 30px;
        font-size: 18px;
        color: var(--ink);
        border-radius: 7px;
        display: inline-flex;
        align-items: center;
        justify-content: center;
      }
      .c-stepper .num {
        min-width: 42px;
        text-align: center;
        font-family: var(--ff-mono);
        font-size: 13px;
      }
      .c-seg {
        display: flex;
        background: var(--panel-2);
        border-radius: var(--radius-xs);
        padding: 3px;
        gap: 2px;
      }
      .c-seg button {
        flex: 1;
        padding: 7px 8px;
        border-radius: 7px;
        background: transparent;
        font-size: 12px;
        font-weight: 500;
        color: var(--ink-2);
        white-space: nowrap;
      }
      .c-seg button.on {
        background: var(--card);
        color: var(--ink);
        box-shadow: 0 1px 3px rgba(0, 0, 0, 0.12);
        font-weight: 600;
      }
      .c-select {
        font-family: var(--ff-mono);
        font-size: 12px;
        color: var(--ink);
        border: 1px solid var(--hairline);
        border-radius: var(--radius-xs);
        padding: 9px 11px;
        background: var(--card-2);
        cursor: pointer;
      }
      .c-badge {
        font-family: var(--ff-mono);
        font-size: 10px;
        letter-spacing: 0.02em;
        text-transform: uppercase;
        padding: 4px 9px;
        border-radius: var(--pill);
        color: var(--muted);
        white-space: nowrap;
        background: var(--panel-2);
      }
      .c-badge.surge {
        color: var(--amber);
        background: color-mix(in oklab, var(--amber) 13%, transparent);
      }
      .c-badge.reserved {
        color: var(--eu);
        background: color-mix(in oklab, var(--eu) 13%, transparent);
      }

      /* Quote panel */
      .c-quote {
        position: sticky;
        top: 92px;
        box-shadow: var(--shadow);
      }
      @media (max-width: 1080px) {
        .c-quote {
          position: static;
        }
      }
      .c-quote-head {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 18px 22px 14px;
      }
      .c-quote-head .t {
        font-family: var(--ff-display);
        font-weight: 600;
        font-size: 19px;
        letter-spacing: -0.02em;
      }
      .c-quote-head .tick {
        width: 9px;
        height: 9px;
        border-radius: 50%;
        background: var(--green);
      }
      .c-quote-section {
        padding: 16px 22px;
        border-top: 1px solid var(--hairline-2);
      }
      .c-quote-section .sh {
        font-size: 12px;
        font-weight: 600;
        color: var(--ink);
        margin-bottom: 12px;
        display: flex;
        justify-content: space-between;
        align-items: center;
      }
      .c-quote-section .sh span:last-child {
        font-family: var(--ff-mono);
        font-size: 11px;
        font-weight: 400;
        color: var(--muted);
      }
      .c-line {
        display: flex;
        justify-content: space-between;
        align-items: baseline;
        gap: 12px;
        padding: 5px 0;
        font-family: var(--ff-mono);
        font-size: 12.5px;
      }
      .c-line .lbl {
        color: var(--ink-2);
        font-family: var(--ff-text);
        font-size: 13px;
      }
      .c-line .amt {
        color: var(--ink);
        white-space: nowrap;
      }
      .c-line.surge .lbl,
      .c-line.surge .amt {
        color: var(--amber);
      }
      .c-line.disc .lbl,
      .c-line.disc .amt {
        color: var(--green);
      }
      .c-line.rule-top {
        border-top: 1px solid var(--hairline-2);
        margin-top: 8px;
        padding-top: 12px;
      }
      .c-line.rule-top .lbl {
        font-weight: 600;
        color: var(--ink);
      }
      .c-premium {
        margin-top: 14px;
        padding: 13px 14px;
        border-radius: var(--radius-sm);
        background: color-mix(in oklab, var(--amber) 9%, transparent);
        font-size: 12.5px;
        line-height: 1.55;
        color: var(--ink-2);
      }
      .c-premium b {
        color: var(--amber);
        font-family: var(--ff-mono);
        font-weight: 600;
      }
      .c-term {
        display: flex;
        background: var(--panel-2);
        border-radius: var(--radius-xs);
        padding: 3px;
        gap: 2px;
      }
      .c-term button {
        flex: 1;
        padding: 8px 4px;
        border-radius: 8px;
        background: transparent;
        font-size: 12px;
        font-weight: 500;
        color: var(--ink-2);
        display: flex;
        flex-direction: column;
        gap: 2px;
        align-items: center;
      }
      .c-term button .d {
        font-size: 9px;
        color: var(--green);
        font-family: var(--ff-mono);
      }
      .c-term button.on {
        background: var(--card);
        color: var(--ink);
        box-shadow: 0 1px 3px rgba(0, 0, 0, 0.12);
        font-weight: 600;
      }
      .c-total {
        padding: 18px 22px 22px;
        border-top: 1px solid var(--hairline-2);
      }
      .c-total .grand {
        display: flex;
        align-items: baseline;
        justify-content: space-between;
      }
      .c-total .grand .lbl {
        font-size: 13px;
        font-weight: 500;
        color: var(--muted);
      }
      .c-total .grand .amt {
        font-family: var(--ff-mono);
        font-weight: 600;
        font-size: 34px;
        letter-spacing: -0.02em;
        color: var(--ink);
      }
      .c-total .grand .amt .permo {
        font-family: var(--ff-mono);
        font-size: 13px;
        font-weight: 400;
        color: var(--muted);
        margin-left: 3px;
      }
      .c-total .qnote {
        font-size: 11.5px;
        color: var(--muted);
        margin-top: 8px;
        line-height: 1.55;
      }
      .c-btn {
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 8px;
        width: 100%;
        padding: 14px 18px;
        font-family: var(--ff-display);
        font-size: 15px;
        font-weight: 600;
        border: 0;
        background: var(--eu-brand);
        color: #fff;
        border-radius: var(--radius-sm);
        margin-top: 16px;
      }
      .c-btn:hover {
        filter: brightness(1.08);
      }
      .c-btn.ghost {
        background: var(--panel-2);
        color: var(--ink);
        margin-top: 9px;
      }
      .c-btn:disabled {
        opacity: 0.4;
        cursor: not-allowed;
      }
      .c-btn .lucide {
        width: 16px;
        height: 16px;
      }

      /* Modal */
      .c-modal-bg {
        position: fixed;
        inset: 0;
        z-index: 200;
        display: none;
        background: color-mix(in oklab, #000 38%, transparent);
        backdrop-filter: blur(8px);
        align-items: flex-start;
        justify-content: center;
        padding: 56px 20px;
        overflow-y: auto;
      }
      .c-modal-bg.open {
        display: flex;
      }
      .c-modal {
        width: 100%;
        max-width: 580px;
        background: var(--card);
        border: 1px solid var(--hairline-2);
        border-radius: var(--radius);
        box-shadow: 0 30px 90px rgba(0, 0, 0, 0.4);
      }
      .c-modal-head {
        display: flex;
        justify-content: space-between;
        align-items: flex-start;
        padding: 24px 26px 18px;
      }
      .c-modal-head .idx {
        font-family: var(--ff-mono);
        font-size: 11px;
        letter-spacing: 0.04em;
        text-transform: uppercase;
        color: var(--muted);
      }
      .c-modal-head h2 {
        margin: 8px 0 0;
        font-family: var(--ff-display);
        font-weight: 600;
        font-size: 26px;
        letter-spacing: -0.03em;
      }
      .c-modal-head .close {
        color: var(--muted);
        width: 32px;
        height: 32px;
        border-radius: 50%;
        display: inline-flex;
        align-items: center;
        justify-content: center;
      }
      .c-modal-head .close:hover {
        color: var(--ink);
        background: var(--panel-2);
      }
      .c-modal-body {
        padding: 0 26px 24px;
      }
      .c-summary {
        border-radius: var(--radius-sm);
        background: var(--panel-2);
        margin-bottom: 22px;
        font-family: var(--ff-mono);
        font-size: 12.5px;
        overflow: hidden;
      }
      .c-summary .sr {
        display: flex;
        justify-content: space-between;
        padding: 11px 16px;
        border-bottom: 1px solid var(--hairline-2);
      }
      .c-summary .sr:last-child {
        border-bottom: 0;
      }
      .c-summary .sr .l {
        color: var(--ink-2);
        font-family: var(--ff-text);
        font-size: 13px;
      }
      .c-summary .sr .r {
        color: var(--ink);
        font-weight: 600;
      }
      .c-summary .sr.total {
        background: color-mix(in oklab, var(--amber) 8%, transparent);
      }
      .c-summary .sr.total .r {
        color: var(--amber);
      }
      .c-form {
        display: grid;
        grid-template-columns: 1fr 1fr;
        gap: 16px;
      }
      .c-field {
        display: flex;
        flex-direction: column;
        gap: 7px;
      }
      .c-field.full {
        grid-column: 1 / -1;
      }
      .c-field label {
        font-size: 12px;
        font-weight: 500;
        color: var(--muted);
      }
      .c-field input,
      .c-field textarea,
      .c-field select {
        font-family: var(--ff-text);
        font-size: 14px;
        color: var(--ink);
        border: 1px solid var(--hairline);
        border-radius: var(--radius-xs);
        padding: 11px 13px;
        background: var(--card-2);
        width: 100%;
      }
      .c-field textarea {
        resize: vertical;
        min-height: 76px;
      }
      .c-modal-foot {
        padding: 18px 26px 22px;
        border-top: 1px solid var(--hairline-2);
        display: flex;
        align-items: center;
        gap: 18px;
        justify-content: space-between;
      }
      .c-modal-foot .fine {
        font-size: 11.5px;
        color: var(--muted);
        line-height: 1.5;
        max-width: 32ch;
      }
      .c-modal-foot .c-btn {
        margin-top: 0;
        width: auto;
        padding-inline: 22px;
      }
      .c-confirm {
        padding: 40px 30px 36px;
        text-align: center;
      }
      .c-confirm .mark {
        width: 56px;
        height: 56px;
        border-radius: 50%;
        background: color-mix(in oklab, var(--green) 16%, transparent);
        color: var(--green);
        display: inline-flex;
        align-items: center;
        justify-content: center;
        margin-bottom: 20px;
      }
      .c-confirm .mark .lucide {
        width: 26px;
        height: 26px;
      }
      .c-confirm h2 {
        margin: 0;
        font-family: var(--ff-display);
        font-weight: 600;
        font-size: 27px;
        letter-spacing: -0.03em;
      }
      .c-confirm p {
        margin: 13px auto 0;
        color: var(--ink-2);
        font-size: 14.5px;
        max-width: 46ch;
        line-height: 1.6;
      }
      .c-confirm .ref {
        font-family: var(--ff-mono);
        margin: 24px auto 0;
        max-width: 380px;
        border-radius: var(--radius-sm);
        background: var(--panel-2);
        overflow: hidden;
        text-align: left;
      }
      .c-confirm .ref .r {
        display: flex;
        justify-content: space-between;
        padding: 12px 16px;
        border-bottom: 1px solid var(--hairline-2);
        font-size: 12.5px;
      }
      .c-confirm .ref .r:last-child {
        border-bottom: 0;
      }
      .c-confirm .ref .r .l {
        color: var(--muted);
        font-family: var(--ff-text);
        font-size: 13px;
      }
      .c-confirm .ref .r .v {
        color: var(--ink);
        font-weight: 600;
      }
      .c-confirm .ref .r .v.q {
        color: var(--amber);
      }

      /* Legal footer stripe */
      .footer-bottom {
        max-width: var(--maxw);
        margin: 0 auto;
        padding: 20px var(--pad-x) 32px;
        border-top: 1px solid var(--hairline-2);
        display: flex;
        justify-content: space-between;
        gap: 24px;
        flex-wrap: wrap;
        font-family: var(--ff-mono);
        font-size: 11px;
        color: var(--muted);
        letter-spacing: 0.02em;
      }
    `,
  ];

  public connectedCallback(): void {
    void super.connectedCallback();
    // The composer starts deliberately empty: nothing is preselected, the visitor
    // picks every component of their stack themselves.
  }

  private navigate(path: string): void {
    this.dispatchEvent(new CustomEvent('ceu-navigate', { detail: { path }, bubbles: true, composed: true }));
  }

  private async toggleTheme(): Promise<void> {
    const dt = await this.domtoolsPromise;
    dt.themeManager.toggleDarkBright();
    try {
      localStorage.setItem('ceu_theme', dt.themeManager.goBrightBoolean ? 'bright' : 'dark');
    } catch (e) {
      /* ignore */
    }
  }

  // ——— state mutations ———
  private add(type: TItemType): void {
    this.stack = [...this.stack, { id: this.uid++, type, ...DEFAULTS[type]() } as IItem];
  }
  private remove(id: number): void {
    this.stack = this.stack.filter((x) => x.id !== id);
  }
  private setField(id: number, field: keyof IItem, value: any): void {
    this.stack = this.stack.map((x) => (x.id === id ? { ...x, [field]: value } : x));
  }
  private step(id: number, field: keyof IItem, delta: number): void {
    const it = this.stack.find((x) => x.id === id);
    if (!it) return;
    this.setField(id, field, Math.max(1, Math.min(99, ((it as any)[field] || 0) + delta)));
  }

  // ——— pricing ———
  private baseMonthly(it: IItem): number {
    return calculateQuoteLine(it).baseMonthly;
  }
  private itemSummary(it: IItem): string {
    switch (it.type) {
      case 'container':
        return `${it.cores} ${it.coreType === 'shared' ? 'shared' : 'dedicated'} vCPU · ${it.mem} GiB · ×${it.replicas}`;
      case 'node':
        return `${it.count} × EPYC 9654 · 96c / 384 GiB`;
      case 'block':
        return `${it.gib} GiB · NVMe, replicated ×2`;
      case 'object':
        return `${it.gib} GiB · S3-compatible`;
      case 'ip':
        return `${it.count} × static IPv4`;
    }
    return '';
  }
  private get quote() {
    if (this.stack.length) return calculateQuote(this.stack, this.term);
    return {
      d: DEMAND[this.demandKey],
      billBase: 0,
      billSurged: 0,
      surcharge: 0,
      billQuoted: 0,
      gaRef: 0,
      premium: 0,
      resQuoted: 0,
      billCount: 0,
      resCount: 0,
    };
  }

  private openModal(): void {
    if (!this.stack.length) return;
    this.modalRef = '';
    this.modalIndicativeUntil = null;
    this.modalError = '';
    this.modalBusy = false;
    this.modalStage = 'form';
    this.modalOpen = true;
  }

  /** Builds a selection-only payload; the server derives all commercial fields. */
  private buildQuotePayload(
    formArg: HTMLFormElement,
  ): interfaces.IReq_SubmitQuoteRequest['request'] {
    const data = new FormData(formArg);
    const readField = (nameArg: string) => String(data.get(nameArg) ?? '').trim();
    return {
      stack: this.stack.map((it) => {
        const item: interfaces.IQuoteStackSelection = {
          type: it.type,
          region: it.region,
        };
        if (it.type === 'container') {
          item.coreType = it.coreType;
          item.cores = it.cores;
          item.mem = it.mem;
          item.replicas = it.replicas;
        }
        if (it.type === 'node' || it.type === 'ip') item.count = it.count;
        if (it.type === 'block' || it.type === 'object') item.gib = it.gib;
        return item;
      }),
      commitmentTerm: this.term,
      contact: {
        organisation: readField('org'),
        email: readField('email'),
        vatId: readField('vat'),
        desiredStart: readField('start'),
        notes: readField('notes'),
      },
      website: readField('website'),
    };
  }

  private async submitQuote(): Promise<void> {
    if (this.modalBusy) return;
    const form = this.shadowRoot?.querySelector('#quoteForm') as HTMLFormElement | null;
    if (!form || !form.reportValidity()) return;
    if (!this.stack.length) {
      this.modalError = 'Add at least one component before requesting a quote.';
      return;
    }
    this.modalBusy = true;
    this.modalError = '';
    try {
      const request = new plugins.domtools.plugins.typedrequest.TypedRequest<interfaces.IReq_SubmitQuoteRequest>(
        '/typedrequest',
        'submitQuoteRequest',
      );
      const response = await request.fire(this.buildQuotePayload(form));
      this.modalRef = response.reference;
      this.modalIndicativeUntil = new Date(response.indicativeUntilIso);
      this.modalStage = 'confirm';
    } catch (error) {
      // Server-issued messages are visitor-safe and specific; anything else (offline,
      // DNS, TLS) gets a generic line instead of a technical one.
      this.modalError = error instanceof plugins.domtools.plugins.typedrequest.TypedResponseError
        ? error.message
        : 'We could not reach the quote service. Please check your connection and try again.';
    } finally {
      this.modalBusy = false;
    }
  }

  private indicativeDate(): string {
    const d = this.modalIndicativeUntil ?? new Date(Date.now() + 7 * 24 * 60 * 60 * 1000);
    return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  }

  // ——— render helpers ———
  private renderItem(it: IItem): TemplateResult {
    const t = TYPES[it.type];
    const base = this.baseMonthly(it);
    const live = isLive(it.region);
    const d = DEMAND[this.demandKey];
    const now = live ? eur(base * d.idx) + '/mo' : eur(base) + '/mo';
    return html`
      <div class="c-item">
        <div class="c-item-head">
          <span class="c-item-sym">${icon(t.sym)}</span>
          <span class="c-item-title">
            <span class="nm">${t.nm}</span>
            <span class="meta">${this.itemSummary(it)} · ${regionById(it.region).code}</span>
          </span>
          <span class="c-badge ${live ? 'surge' : 'reserved'}">${live ? `surge ×${d.idx.toFixed(2)}` : 'in build'}</span>
          <span class="c-item-price">
            <span class="now">${now}</span>
            <span class="base">${live ? html`<s>${eur(base)}</s> list` : `at GA · ${regionById(it.region).eta}`}</span>
          </span>
          <button class="c-item-rm" @click=${() => this.remove(it.id)} aria-label="Remove">${icon('X')}</button>
        </div>
        <div class="c-item-body">
          ${it.type === 'container'
            ? html`
                <div class="c-ctl span6">
                  <div class="c-ctl-key"><span>Core type</span></div>
                  <div class="c-seg">
                    <button class=${it.coreType === 'shared' ? 'on' : ''} @click=${() => this.setField(it.id, 'coreType', 'shared')}>Shared · €${CATALOG.shared.toFixed(2)}</button>
                    <button class=${it.coreType === 'dedicated' ? 'on' : ''} @click=${() => this.setField(it.id, 'coreType', 'dedicated')}>Dedicated · €${CATALOG.dedicated.toFixed(2)}</button>
                  </div>
                </div>
                ${this.stepper(it, 'replicas', 'Replicas')}
                ${this.slider(it, 'cores', 'vCPU', 1, 64, 1, 'vCPU', 'span3')}
                ${this.slider(it, 'mem', 'Memory', 1, 256, 1, 'GiB', 'span3')}
              `
            : ''}
          ${it.type === 'node' ? this.stepper(it, 'count', 'Nodes') : ''}
          ${it.type === 'block' ? this.slider(it, 'gib', 'Capacity', 10, 4000, 10, 'GiB', 'span6') : ''}
          ${it.type === 'object' ? this.slider(it, 'gib', 'Capacity', 50, 10000, 50, 'GiB', 'span6') : ''}
          ${it.type === 'ip' ? this.stepper(it, 'count', 'Addresses') : ''}
          <div class="c-ctl span6">
            <div class="c-ctl-key"><span>Target region</span></div>
            <select class="c-select" @change=${(e: Event) => this.setField(it.id, 'region', (e.target as HTMLSelectElement).value)}>
              ${REGIONS.map(
                (r) => html`<option value=${r.id} ?selected=${r.id === it.region}>${r.code} — ${r.city} (${r.state === 'live' ? 'live' : 'build · ' + r.eta})</option>`,
              )}
            </select>
          </div>
        </div>
      </div>
    `;
  }
  private slider(it: IItem, field: keyof IItem, label: string, min: number, max: number, step: number, unit: string, span: string): TemplateResult {
    return html`
      <div class="c-ctl ${span}">
        <div class="c-ctl-key"><span>${label}</span><span class="v">${(it as any)[field]} ${unit}</span></div>
        <input type="range" min=${min} max=${max} step=${step} .value=${String((it as any)[field])} @input=${(e: Event) => this.setField(it.id, field, +(e.target as HTMLInputElement).value)} />
      </div>
    `;
  }
  private stepper(it: IItem, field: keyof IItem, label: string): TemplateResult {
    return html`
      <div class="c-ctl span6">
        <div class="c-ctl-key"><span>${label}</span></div>
        <div class="c-stepper">
          <button @click=${() => this.step(it.id, field, -1)}>−</button>
          <span class="num">${(it as any)[field]}</span>
          <button @click=${() => this.step(it.id, field, 1)}>+</button>
        </div>
      </div>
    `;
  }

  public render(): TemplateResult {
    const q = this.quote;
    return html`
      <header class="c-top">
        <div class="c-top-inner">
          <a class="c-brand" href="/" @click=${(e: Event) => { e.preventDefault(); this.navigate('/'); }}>
            <span class="dot"></span><span>central</span><span class="tld">.eu</span>
            <span class="seg-label">console</span>
          </a>
          <span class="c-env"><span class="pip"></span> region <b>BRE1</b> · bremen</span>
          <div class="c-top-right">
            <span class="c-ea">Early access</span>
            <button class="c-iconbtn" @click=${() => this.toggleTheme()} title="Toggle theme">${icon('Contrast')}</button>
            <span class="c-acct"><span class="ava">TV</span> task.vc</span>
          </div>
        </div>
      </header>

      <div class="c-advisory-inner">
        <div class="c-advisory-card">
          <div>
            <div class="c-adv-tag"><span class="pulse-dot"></span> <span>${q.d.tag}</span></div>
            <div class="c-adv-text">
              BRE1, our only live owned site, is <b>running near full</b>. Request a quote for
              <b>allocation review</b> as we bring new EU capacity online.
              Live quotes reflect current demand; future regions are shown at indicative GA list price, no demand premium.
            </div>
          </div>
          <div class="c-adv-idx"><span class="big">×${q.d.idx.toFixed(2)}</span><span class="lbl">demand multiplier</span></div>
        </div>
      </div>

      <main class="c-main">
        <section class="c-composer">
          <div class="c-head">
            <div class="idx">CON/01 — Stack composer</div>
            <h1>Compose a cloud stack.</h1>
            <p>Build your bill of materials from owned EU metal. BRE1 is near full, so live allocations are priced to current demand. Request a quote for allocation review — components targeting in-build regions are quoted at GA list price, no surge.</p>
          </div>

          <div class="c-capboard">
            <div class="c-capboard-head"><span class="t">Live capacity</span><span class="mut">indicative view</span></div>
            ${REGIONS.map((r) => {
              const pct = r.state === 'live' ? Math.round(q.d.util * 100) : null;
              const cls = pct === null ? '' : pct >= 97 ? 'crit' : pct >= 90 ? 'hot' : '';
              return html`
                <div class="c-caprow">
                  <span class="rc"><span class="code">${r.code}</span><span class="city">${r.city}</span></span>
                  <span class="c-captrack"><span class="c-capfill ${cls}" style="width:${pct === null ? 6 : pct}%"></span></span>
                  <span class="rstate">
                    <span class="pct">${pct === null ? '—' : pct + '%'}</span>
                    <span class="tag ${r.state === 'live' ? 'live' : 'build'}">${r.state === 'live' ? 'live · ' + r.note : 'build · ' + r.eta}</span>
                  </span>
                </div>
              `;
            })}
          </div>

          <div class="c-palette">
            <div class="c-palette-label">Add component</div>
            ${(Object.keys(TYPES) as TItemType[]).map(
              (type) => html`
                <button class="c-add" @click=${() => this.add(type)}>
                  <span class="sym">${icon(TYPES[type].sym)}</span>
                  <span class="nm">${TYPES[type].nm}</span>
                  <span class="pr">${TYPES[type].pr}</span>
                </button>
              `,
            )}
          </div>

          <div class="c-bom">
            ${this.stack.length
              ? this.stack.map((it) => this.renderItem(it))
              : html`
                  <div class="c-bom-empty">
                    <span class="sym">${icon('Layers')}</span>
                    <div class="t">Nothing in your stack yet</div>
                    <p>
                      Start from any component above — containers, dedicated nodes, storage or public
                      IPv4. Add as many as you need; the quote on the right updates as you compose.
                    </p>
                    <span class="hint">${icon('ArrowUp')} Pick a component to begin</span>
                  </div>
                `}
          </div>
        </section>

        <aside>
          <div class="c-quote">
            <div class="c-quote-head"><span class="t">Quote</span><span class="tick ${this.stack.length ? '' : 'idle'}"></span></div>
            ${this.stack.length
              ? html`
                  <div class="c-quote-section">
                    <div class="sh"><span>Billable now · BRE1</span><span>${q.billCount} ${q.billCount === 1 ? 'component' : 'components'}</span></div>
                    <div class="c-line"><span class="lbl">List subtotal</span><span class="amt">${eur(q.billBase)}</span></div>
                    <div class="c-line surge"><span class="lbl">Demand surcharge · ×${q.d.idx.toFixed(2)}</span><span class="amt">+${eur(q.surcharge)}</span></div>
                    ${this.term > 0 ? html`<div class="c-line disc"><span class="lbl">Commitment discount · −${Math.round(this.term * 100)}%</span><span class="amt">−${eur(q.billSurged - q.billQuoted)}</span></div>` : ''}
                    <div class="c-line rule-top"><span class="lbl"><b>Quoted monthly</b></span><span class="amt"><b>${eur(q.billQuoted)}</b></span></div>
                    ${q.billBase > 0 ? html`<div class="c-premium">At GA list this stack is <b>${eur(q.gaRef)}</b>/mo. Current demand adds <b>${eur(q.premium)}</b>/mo (+${Math.round((q.d.idx - 1) * 100)}%) — the premium clears when capacity does.</div>` : ''}
                  </div>
                `
              : html`
                  <div class="c-quote-empty">
                    <div class="t">Nothing quoted yet</div>
                    <p>
                      Add components on the left and this panel fills in — list price, the current
                      demand surcharge for live capacity, and your commitment discount.
                    </p>
                  </div>
                `}
            ${q.resCount > 0 ? html`
              <div class="c-quote-section">
                <div class="sh"><span>In-build regions · indicative GA</span><span>${q.resCount} ${q.resCount === 1 ? 'component' : 'components'}</span></div>
                <div class="c-line"><span class="lbl">Indicative subtotal</span><span class="amt">${eur(q.resQuoted)}</span></div>
                <div class="c-line"><span class="lbl">Availability</span><span class="amt">subject to region launch</span></div>
              </div>` : ''}
            <div class="c-quote-section">
              <div class="sh"><span>Commitment term</span><span>lowers your rate</span></div>
              <div class="c-term">
                <button class=${this.term === 0 ? 'on' : ''} @click=${() => (this.term = 0)}>On-demand<span class="d">&nbsp;</span></button>
                <button class=${this.term === 0.1 ? 'on' : ''} @click=${() => (this.term = 0.1)}>3 mo<span class="d">−10%</span></button>
                <button class=${this.term === 0.2 ? 'on' : ''} @click=${() => (this.term = 0.2)}>6 mo<span class="d">−20%</span></button>
                <button class=${this.term === 0.35 ? 'on' : ''} @click=${() => (this.term = 0.35)}>12 mo<span class="d">−35%</span></button>
              </div>
            </div>
            <div class="c-total">
              <div class="grand ${this.stack.length ? '' : 'idle'}"><span class="lbl">Quoted /mo</span><span class="amt">${this.stack.length ? html`${eur(q.billQuoted)}<span class="permo">/mo</span>` : '—'}</span></div>
              <div class="qnote">${this.stack.length
                ? 'Indicative & non-binding · excl. VAT · subject to capacity confirmation. Pricing snapshot shown for a seven-day review window. VAT may apply depending on your location.'
                : 'Add your first component to compose a stack and request a quote.'}</div>
              <button class="c-btn" ?disabled=${!this.stack.length} @click=${() => this.openModal()}>Request a quote ${icon('ArrowRight')}</button>
              ${this.stack.length ? html`<button class="c-btn ghost" @click=${() => (this.stack = [])}>Clear stack</button>` : ''}
            </div>
          </div>
        </aside>
      </main>

      <div class="footer-bottom">
        <span>© 2026 Task Venture Capital GmbH · Amtsgericht Bremen · HRB 35230 HB · <a class="link" href="https://legal.task.vc" target="_blank">legal.task.vc</a></span>
        <span>v2026.04 · built in the union</span>
      </div>

      <div class="c-modal-bg ${this.modalOpen ? 'open' : ''}" @click=${(e: Event) => { if (e.target === e.currentTarget) this.modalOpen = false; }}>
        <div class="c-modal">${this.modalStage === 'form' ? this.renderModalForm(q) : this.renderConfirm(q)}</div>
      </div>
    `;
  }

  private renderModalForm(q: ReturnType<CeuConsole['quote']> extends never ? any : any): TemplateResult {
    return html`
      <div class="c-modal-head">
        <div><div class="idx">CON/02 — Allocation review</div><h2>Request a quote</h2></div>
        <button class="close" @click=${() => (this.modalOpen = false)}>${icon('X')}</button>
      </div>
      <div class="c-modal-body">
        <div class="c-summary">
          <div class="sr"><span class="l">Components</span><span class="r">${this.stack.length} (${q.billCount} live · ${q.resCount} in build)</span></div>
          <div class="sr"><span class="l">GA list · billable now</span><span class="r">${eur(q.gaRef)}/mo</span></div>
          <div class="sr"><span class="l">Demand premium · ×${q.d.idx.toFixed(2)}</span><span class="r">+${eur(q.premium)}/mo</span></div>
          <div class="sr total"><span class="l">Indicative &amp; non-binding</span><span class="r">${eur(q.billQuoted)}/mo</span></div>
        </div>
        <form class="c-form" id="quoteForm" @submit=${(e: Event) => e.preventDefault()}>
          <div class="c-field"><label>Organisation</label><input name="org" required placeholder="Acme GmbH" /></div>
          <div class="c-field"><label>Work email</label><input name="email" type="email" required placeholder="you@acme.eu" /></div>
          <div class="c-field"><label>EU VAT ID (optional)</label><input name="vat" placeholder="DE123456789" /></div>
          <div class="c-field"><label>Desired start</label><select name="start"><option>As soon as capacity allows</option><option>Within 30 days</option><option>This quarter</option><option>At general availability</option></select></div>
          <div class="c-field full"><label>Workload &amp; residency notes</label><textarea name="notes" placeholder="What runs here, expected growth, residency constraints…"></textarea></div>
          <input name="website" tabindex="-1" autocomplete="off" aria-hidden="true" style="position:absolute;left:-9999px;width:1px;height:1px;opacity:0" />
          ${this.modalError
            ? html`<div class="c-form-error" role="alert">${icon('TriangleAlert')}<span>${this.modalError}</span></div>`
            : ''}
        </form>
      </div>
      <div class="c-modal-foot">
        <span class="fine">Submitting requests an allocation review. Any binding quote we issue by email is subject to available capacity; no charge until you accept a final quote.</span>
        <button class="c-btn" ?disabled=${this.modalBusy} @click=${() => void this.submitQuote()}>
          ${this.modalBusy ? 'Submitting…' : html`Submit quote request ${icon('ArrowRight')}`}
        </button>
      </div>
    `;
  }

  private renderConfirm(q: any): TemplateResult {
    return html`
      <div class="c-confirm">
        <div class="mark">${icon('Check')}</div>
        <h2>Quote request received.</h2>
        <p>We will review your requested allocation against available capacity. An engineer emails a binding quote within one working day, subject to available capacity; no charge until you accept a final quote.</p>
        <div class="ref">
          <div class="r"><span class="l">Quote reference</span><span class="v">${this.modalRef}</span></div>
          <div class="r"><span class="l">Allocation review</span><span class="v q">pending</span></div>
          <div class="r"><span class="l">Indicative monthly</span><span class="v">${eur(q.billQuoted)}/mo</span></div>
          <div class="r"><span class="l">Indicative review through</span><span class="v">${this.indicativeDate()}</span></div>
        </div>
        <button class="c-btn" style="max-width:240px; margin:24px auto 0;" @click=${() => (this.modalOpen = false)}>Back to console</button>
      </div>
    `;
  }
}
